import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  CircularProgress,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  Chip,
  Tooltip,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import useFeedbackModal from '../../../hooks/useFeedbackModal';

const PersonItem = ({
  person,
  index,
  sectionKey,
  onUpdate,
  onRemove,
  onSearchPerson,
  onContactChange,
  getOtherRoles,
  showRole,
  showContact,
  errors
}) => {
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  const handleCuilSearch = async () => {
    if (!person.cuil || person.cuil.length !== 11) {
      setSearchError('Son necesarios 11 caracteres numéricos');
      onUpdate(index, {
        ...person,
        nombre: '',
        apellido: '',
        mail: '',
        celular: ''
      });
      return;
    }
    setSearchError('');
    setIsSearching(true);

    try {
      let response;
      if (onSearchPerson) {
        response = await onSearchPerson(person.cuil);
      }

      if (response && response.success) {
        onUpdate(index, {
          ...person,
          nombre: response.data.nombre || '',
          apellido: response.data.apellido || '',
          mail: response.data.mail || response.data.correo || '',
          celular: response.data.celular || ''
        });
      } else {
        setSearchError(response?.message || 'Persona no encontrada');
        onUpdate(index, {
          ...person,
          nombre: '',
          apellido: '',
          mail: '',
          celular: ''
        });
      }
    } catch (err) {
      setSearchError('Error al buscar.');
      onUpdate(index, {
        ...person,
        nombre: '',
        apellido: '',
        mail: '',
        celular: ''
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleCuilChange = (e) => {
    const rawValue = e.target.value;
    const numericCuil = rawValue.replace(/\D/g, '').slice(0, 11);
    setSearchError('');
    // Al cambiar el CUIL, limpiar los datos autocompletados
    onUpdate(index, {
      ...person,
      cuil: numericCuil,
      nombre: '',
      apellido: '',
      mail: '',
      celular: ''
    });
  };

  const handleContactFieldChange = (field) => (e) => {
    let newValue = e.target.value;
    if (field === 'celular') {
      newValue = newValue.replace(/\D/g, '').slice(0, 10);
    }
    // 1. Actualizar estado local del ítem
    onUpdate(index, { ...person, [field]: newValue });
    // 2. Sincronizar globalmente por CUIL
    if (person.cuil && onContactChange) {
      onContactChange(person.cuil, field, newValue);
    }
  };

  const handleChangeField = (field) => (e) => {
    onUpdate(index, { ...person, [field]: e.target.value });
  };

  const otherRoles = getOtherRoles ? getOtherRoles(person.cuil, sectionKey, index) : [];
  const isSynced = otherRoles && otherRoles.length > 0;
  const isCuilIncomplete = Boolean(person.cuil && person.cuil.length > 0 && person.cuil.length < 11);
  const cuilHelperText = errors?.cuil || searchError || (isCuilIncomplete ? 'Son necesarios 11 caracteres numéricos' : '');
  const hasCuilError = Boolean(errors?.cuil || searchError || isCuilIncomplete);

  return (
    <Box
      sx={{
        mb: 2.5,
        p: 2.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#FFFFFF',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: 'primary.light',
          boxShadow: '0 2px 8px rgba(0, 154, 218, 0.08)'
        }
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={showRole ? 6 : 4}>
          <TextField
            fullWidth
            label={<span>CUIL {!person.cuil?.trim() && <span style={{ color: '#E2464C' }}>*</span>}</span>}
            value={person.cuil || ''}
            onChange={handleCuilChange}
            error={hasCuilError}
            helperText={cuilHelperText}
            inputProps={{
              maxLength: 11,
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCuilSearch} edge="end" disabled={isSearching || isCuilIncomplete}>
                    {isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          {isSynced && (
            <Box sx={{ mt: 0.75 }}>
              <Tooltip title={`Esta persona también figura como: ${otherRoles.join(', ')}. Los datos de contacto están sincronizados.`}>
                <Chip
                  icon={<SyncIcon />}
                  label={`Sincronizado con: ${otherRoles.join(', ')}`}
                  color="info"
                  variant="outlined"
                  size="small"
                  sx={{
                    fontWeight: 'medium',
                    maxWidth: '100%',
                    height: 'auto',
                    '& .MuiChip-label': {
                      display: 'block',
                      whiteSpace: 'normal',
                      py: 0.5,
                    },
                  }}
                />
              </Tooltip>
            </Box>
          )}
        </Grid>

        {showRole && (
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors?.rol}>
              <InputLabel>Rol {!person.rol && <span style={{ color: '#E2464C' }}>*</span>}</InputLabel>
              <Select
                value={person.rol || ''}
                label={`Rol ${!person.rol ? '*' : ''}`}
                onChange={handleChangeField('rol')}
              >
                <MenuItem value="CPE">Con permiso de edición</MenuItem>
                <MenuItem value="SPE">Sin permiso de edición</MenuItem>
              </Select>
              {errors?.rol && <FormHelperText>{errors.rol}</FormHelperText>}
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Nombre"
            value={person.nombre || ''}
            disabled={true}
            error={!!errors?.nombre}
            helperText={errors?.nombre || 'Obtenido automáticamente por CUIL'}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Apellido"
            value={person.apellido || ''}
            disabled={true}
            error={!!errors?.apellido}
            helperText={errors?.apellido || 'Obtenido automáticamente por CUIL'}
          />
        </Grid>

        {showContact && (
          <>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Correo Electrónico"
                type="email"
                value={person.mail || person.correo || ''}
                onChange={handleContactFieldChange('mail')}
                disabled={true}
                error={!!errors?.mail}
                helperText={errors?.mail || 'Obtenido automáticamente por CUIL'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Celular"
                placeholder="Opcional"
                value={person.celular || ''}
                onChange={handleContactFieldChange('celular')}
                disabled={isSearching}
                error={!!errors?.celular}
                helperText={errors?.celular || 'Máximo 10 dígitos (ej: 3511234567)'}
                inputProps={{
                  maxLength: 10,
                  inputMode: 'numeric',
                  pattern: '[0-9]*'
                }}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
          <Button
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => onRemove(index)}
            size="small"
          >
            Quitar
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

const DynamicPersonSection = ({
  title,
  description,
  sectionKey = 'tutores',
  persons = [],
  onChange,
  onSearchPerson,
  onContactChange,
  getOtherRoles,
  showRole,
  showContact,
  errors,
  sectionError,
  containerRef,
}) => {
  const { showConfirm } = useFeedbackModal();

  const handleAddPerson = () => {
    const newPerson = { cuil: '', nombre: '', apellido: '', mail: '', celular: '' };
    if (showRole) {
      newPerson.rol = '';
    }
    // Sumarse arriba en la lista
    onChange([newPerson, ...persons]);
  };

  const handleUpdatePerson = (index, updatedPerson) => {
    const newPersons = [...persons];
    newPersons[index] = updatedPerson;
    onChange(newPersons);
  };

  const handleRemovePerson = async (index) => {
    const personToRemove = persons[index];
    const personIdentifier = personToRemove?.nombre && personToRemove?.apellido
      ? `${personToRemove.nombre} ${personToRemove.apellido}`
      : personToRemove?.cuil
        ? `CUIL ${personToRemove.cuil}`
        : 'esta persona';

    const confirmed = await showConfirm({
      title: '¿Estás seguro?',
      description: `Se eliminará a ${personIdentifier} de la lista.`,
      confirmText: 'Aceptar',
      cancelText: 'Cancelar',
    });

    if (!confirmed) return;

    const newPersons = persons.filter((_, i) => i !== index);
    onChange(newPersons);
  };

  return (
    <Card ref={containerRef} variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" color="primary">
            {title}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddPerson}
            size="small"
            sx={{ fontWeight: 'bold', textTransform: 'uppercase' }}
          >
            Agregar Persona
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {description}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Los campos marcados con <span style={{ color: '#E2464C' }}>*</span> son obligatorios.
        </Typography>

        {sectionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {sectionError}
          </Alert>
        )}

        {persons.map((person, index) => (
          <PersonItem
            key={index}
            index={index}
            sectionKey={sectionKey}
            person={person}
            onUpdate={handleUpdatePerson}
            onRemove={handleRemovePerson}
            onSearchPerson={onSearchPerson}
            onContactChange={onContactChange}
            getOtherRoles={getOtherRoles}
            showRole={showRole}
            showContact={showContact}
            errors={errors && errors[index] ? errors[index] : {}}
          />
        ))}
      </CardContent>
    </Card>
  );
};

export default DynamicPersonSection;
